import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Clock, AlertTriangle, RefreshCw, Camera, Heart, Users, FileText } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const Policies = ({ user, onLogout }) => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const policies = [
    {
      icon: Shield,
      title: 'Safety & Supervision',
      color: 'cyan',
      content: [
        'All swimming activities are supervised by certified lifeguards and instructors.',
        'Children under 12 must be accompanied by a parent or guardian who remains on the premises.',
        'All swimmers must shower before entering the pool.',
        'No diving in shallow areas. Diving is only permitted in designated deep zones.',
        'Running on pool deck is strictly prohibited.',
        'Glass containers and food are not allowed in the pool area.',
        'Swimmers with open wounds, infections, or contagious conditions are not permitted to use the pool.'
      ]
    },
    {
      icon: Clock,
      title: 'Booking & Cancellation Policy',
      color: 'blue',
      content: [
        'Lessons must be booked at least 24 hours in advance.',
        'Cancellations made at least 24 hours before the scheduled lesson will receive full credit.',
        'Cancellations made less than 24 hours before will forfeit the session.',
        'No-shows will be counted as completed sessions with no refund.',
        'Rescheduling is free if done at least 24 hours in advance.',
        'In case of instructor absence or facility closure, affected sessions will be rescheduled or credited.',
        'Package sessions are valid for 6 months from the purchase date.'
      ]
    },
    {
      icon: RefreshCw,
      title: 'Refund Policy',
      color: 'green',
      content: [
        'Full refunds are available within 7 days of purchase if no sessions have been used.',
        'Partial refunds may be issued for unused sessions in packages (less a 10% administrative fee).',
        'Registration fees for Marlins Swim Team are non-refundable as they cover merchandise costs.',
        'Refund requests must be submitted in writing via email to info@streamlineaquatics.co.',
        'Refunds will be processed within 14 business days.',
        'Medical exemptions: Full refunds may be granted with valid medical documentation.'
      ]
    },
    {
      icon: AlertTriangle,
      title: 'Liability Waiver',
      color: 'orange',
      content: [
        'All participants (or their guardians) must sign a liability waiver before participating.',
        'Streamline Aquatics Limited takes all reasonable precautions for safety, but swimming carries inherent risks.',
        'Participants acknowledge that injuries may occur despite proper supervision.',
        'Streamline Aquatics is not liable for injuries resulting from participant negligence or failure to follow safety rules.',
        'In cases of gross negligence by Streamline Aquatics staff, standard legal liability applies.',
        'Parents/guardians are responsible for informing staff of any medical conditions or special needs.'
      ]
    },
    {
      icon: Camera,
      title: 'Photography & Media Policy',
      color: 'purple',
      content: [
        'Streamline Aquatics may take photographs and videos during lessons and events for promotional purposes.',
        'Parents/guardians may opt-out of media consent at the time of registration.',
        'Personal photography by parents is permitted for their own children only.',
        'Commercial photography requires prior written approval from management.',
        'Any photographs that include other participants require their consent before sharing publicly.',
        'We respect privacy - please ask before photographing other swimmers or their children.'
      ]
    },
    {
      icon: Heart,
      title: 'Health & Medical Policy',
      color: 'red',
      content: [
        'Participants must disclose any medical conditions that may affect their ability to swim safely.',
        'Swimmers with epilepsy, heart conditions, or other serious medical issues must obtain doctor&apos;s clearance.',
        'Children should not swim within 1 hour of a heavy meal.',
        'If you feel unwell, please do not attend - we will reschedule your session.',
        'First aid trained staff and AED equipment are available on-site.',
        'In case of emergency, staff will administer first aid and contact emergency services as needed.'
      ]
    },
    {
      icon: Users,
      title: 'Code of Conduct',
      color: 'indigo',
      content: [
        'Treat all staff, instructors, and fellow swimmers with respect and courtesy.',
        'Follow all instructions from lifeguards and instructors promptly.',
        'Appropriate swimwear must be worn at all times - no street clothes in the pool.',
        'Bullying, harassment, or discriminatory behavior will not be tolerated.',
        'Parents should support a positive learning environment and avoid distracting children during lessons.',
        'Violations of the code of conduct may result in suspension or termination of membership.'
      ]
    },
    {
      icon: FileText,
      title: 'Data Protection & Privacy',
      color: 'slate',
      content: [
        'Personal information is collected solely for service delivery and communication purposes.',
        'We do not share personal data with third parties except as required for payment processing.',
        'Parents may request access to, correction of, or deletion of their data at any time.',
        'Communication preferences can be updated by contacting us directly.',
        'Our systems use industry-standard security measures to protect your information.',
        'For full details, please request our complete Privacy Policy document.'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} onLogout={onLogout} />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-100 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeIn} className="text-center">
            <h1 className="text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
              Policies & Guidelines
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Your safety and satisfaction are our top priorities. Please review our policies to ensure 
              a safe and enjoyable experience at Streamline Aquatics.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { label: '24hr', desc: 'Cancellation Notice' },
              { label: '6 months', desc: 'Package Validity' },
              { label: '7 days', desc: 'Refund Window' },
              { label: '14 days', desc: 'Refund Processing' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className="p-4"
              >
                <div className="text-3xl font-bold text-cyan-600">{item.label}</div>
                <div className="text-sm text-slate-600">{item.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Policies Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-8">
            {policies.map((policy, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className={`border-2 border-${policy.color}-100 hover:border-${policy.color}-200 transition-all`}>
                  <CardHeader className={`bg-${policy.color}-50`}>
                    <CardTitle className={`flex items-center text-${policy.color}-700 text-xl`}>
                      <policy.icon className="w-6 h-6 mr-3" />
                      {policy.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <ul className="space-y-3">
                      {policy.content.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <span className={`w-2 h-2 mt-2 mr-3 rounded-full bg-${policy.color}-400 flex-shrink-0`}></span>
                          <span className="text-slate-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeIn}>
            <AlertTriangle className="w-12 h-12 mx-auto mb-6 text-yellow-400" />
            <h2 className="text-3xl font-bold mb-4">Important Notice</h2>
            <p className="text-lg text-slate-300 mb-6 leading-relaxed">
              By booking a lesson or registering with Streamline Aquatics, you acknowledge that you have read, 
              understood, and agree to abide by all policies outlined on this page. These policies are designed 
              to ensure the safety and well-being of all our swimmers and staff.
            </p>
            <p className="text-slate-400">
              Last updated: January 2025 | For questions about our policies, please contact us at{' '}
              <a href="mailto:info@streamlineaquatics.co" className="text-cyan-400 hover:underline">
                info@streamlineaquatics.co
              </a>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact for Clarification */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Need Clarification?</h2>
          <p className="text-lg text-slate-600 mb-8">
            If you have any questions about our policies or need special accommodations, 
            please don&apos;t hesitate to reach out to us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="mailto:info@streamlineaquatics.co"
              className="inline-flex items-center justify-center px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Email Us
            </a>
            <a 
              href="/contact"
              className="inline-flex items-center justify-center px-6 py-3 border-2 border-cyan-600 text-cyan-600 rounded-lg hover:bg-cyan-50 transition-colors"
            >
              Contact Page
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Policies;
